import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cardsGridSx, sectionTitleSx, viewToggleButtonSx, viewToggleTextSx } from '../styles';
import { CentralDashboardSectionData } from '../CentralDashboard';
import CentralDashboardCard from './CentralDashboardCard';

const INITIAL_CARDS_COUNT = 6;

type CentralDashboardSectionProps = {
  section: CentralDashboardSectionData;
  onViewMore?: () => void;
};

export default function CentralDashboardSection({ section, onViewMore }: Readonly<CentralDashboardSectionProps>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { cards, hasMorePages, isLoading } = section;

  // Generate skeleton cards if loading and no cards exist
  const skeletonCards: typeof cards = useMemo(() => {
    if (!isLoading || cards.length > 0) return [];
    return Array.from({ length: 6 }).map((_, index) => ({
      id: `skeleton-${index}`,
      title: '',
      description: '',
      isLoading: true,
      moreOptionsMenuItems: undefined,
      testId: `skeleton-card-${index}`,
    }));
  }, [isLoading, cards.length]);

  const displayCards = section.isLoading && cards.length === 0 ? skeletonCards : cards;
  const hasCards = displayCards.length > 0;

  const visibleCards = useMemo(
    () => (expanded ? displayCards : displayCards.slice(0, INITIAL_CARDS_COUNT)),
    [expanded, displayCards]
  );

  const hasExpandableCards = cards.length > INITIAL_CARDS_COUNT;
  const hasMorePagesToFetch = hasMorePages ?? false;
  const hasMoreContent = hasMorePagesToFetch || hasExpandableCards;
  const shouldShowToggle = hasMoreContent;
  const showViewLess = expanded && (!onViewMore || !hasMorePagesToFetch);

  useEffect(() => {
    if (!hasMoreContent && expanded) {
      setExpanded(false);
    }
  }, [expanded, hasMoreContent]);

  return (
    <Grid item xs={12} data-testid={`central-dashboard-section-${section.id}`}>
      <Typography sx={sectionTitleSx} variant='h6' color='text.primary'>
        {section.title}
      </Typography>
      {hasCards ? (
        <Box sx={cardsGridSx}>
          {visibleCards.map((card) => (
            <CentralDashboardCard
              key={card.id}
              description={card.description}
              title={card.title}
              url={card.url ?? ''}
              moreOptionsMenuItems={card.moreOptionsMenuItems}
              hideCategoryIcons
              isLoading={card.isLoading}
            />
          ))}
        </Box>
      ) : null}

      {shouldShowToggle ? (
        <Button
          onClick={() => {
            if (showViewLess) {
              setExpanded(false);
              return;
            }

            if (!expanded) {
              setExpanded(true);
            }

            if (onViewMore && hasMorePagesToFetch) {
              onViewMore();
            }
          }}
          data-testid='view-button'
          sx={viewToggleButtonSx}
        >
          <Typography variant='button' sx={viewToggleTextSx}>
            {showViewLess ? t('admin.centralReportsPage.viewLess') : t('admin.centralReportsPage.viewMore')}
          </Typography>
          {showViewLess ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Button>
      ) : null}
    </Grid>
  );
}

CentralDashboardSection.defaultProps = {
  onViewMore: undefined,
};
